import { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enhanced caching for Phase 2 optimization
  const [registeredHikes, setRegisteredHikes] = useState([]); // Array of hike IDs for quick lookup
  const [registeredHikesData, setRegisteredHikesData] = useState([]); // Full hike objects for MyHikesPage
  const [favoriteHikeIds, setFavoriteHikeIds] = useState(new Set());
  const [participationMap, setParticipationMap] = useState({});
  const [organizedHikes, setOrganizedHikes] = useState([]);
  const [permissions, setPermissions] = useState([]); // User's permission names

  // NEW: Load user profile with all related data in a SINGLE API call
  const loadUserProfile = async (authToken) => {
    if (!authToken) return;

    try {
      // Use new optimized endpoint with include parameter
      const response = await fetch(
        `${API_URL}/auth/me?include=registered_hikes,favorites,organized_hikes`,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);

        // Extract and cache all related data
        if (userData.registered_hikes) {
          setRegisteredHikesData(userData.registered_hikes); // Store full objects
          setRegisteredHikes(userData.registered_hikes.map(h => h.id)); // Store IDs

          // Build participation map from registered hikes
          const pMap = {};
          userData.registered_hikes.forEach(hike => {
            pMap[hike.id] = hike.participation_status;
          });
          setParticipationMap(pMap);
        }

        if (userData.favorite_hike_ids) {
          setFavoriteHikeIds(new Set(userData.favorite_hike_ids));
        }

        if (userData.organized_hikes) {
          setOrganizedHikes(userData.organized_hikes);
        }

        // Store user permissions
        if (userData.permissions) {
          setPermissions(userData.permissions);
        }
      } else {
        throw new Error('Failed to load user profile');
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      throw error;
    }
  };

  const registerForHike = async (hikeId, formResponses = null) => {
    if (!token) return;

    try {
      const requestOptions = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      // Include form responses in body if provided (only add Content-Type when we have a body)
      if (formResponses) {
        requestOptions.headers['Content-Type'] = 'application/json';
        requestOptions.body = JSON.stringify({ form_responses: formResponses });
      }

      const response = await fetch(`${API_URL}/hikes/${hikeId}/register`, requestOptions);

      if (response.ok) {
        const data = await response.json();

        // Update cache optimistically
        setRegisteredHikes([...registeredHikes, hikeId]);
        setParticipationMap(prev => ({
          ...prev,
          [hikeId]: data.request?.stato || 'in_attesa'
        }));
      } else {
        const error = await response.json();
        throw new Error(error.detail);
      }
    } catch (error) {
      console.error('Failed to register for hike:', error);
      throw error;
    }
  };

  const unregisterFromHike = async (hikeId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/hikes/${hikeId}/register`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Update cache
        setRegisteredHikes(registeredHikes.filter(id => id !== hikeId));
        setParticipationMap(prev => {
          const newMap = { ...prev };
          delete newMap[hikeId];
          return newMap;
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail);
      }
    } catch (error) {
      console.error('Failed to unregister from hike:', error);
      throw error;
    }
  };

  // NEW: Toggle favorite with optimistic update and cache management
  const toggleFavorite = async (hikeId) => {
    if (!token) return;

    // Optimistic update
    const wasFavorited = favoriteHikeIds.has(hikeId);
    const newFavoriteIds = new Set(favoriteHikeIds);

    if (wasFavorited) {
      newFavoriteIds.delete(hikeId);
    } else {
      newFavoriteIds.add(hikeId);
    }
    setFavoriteHikeIds(newFavoriteIds);

    try {
      const response = await fetch(`${API_URL}/hikes/${hikeId}/favorite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        // Revert on error
        setFavoriteHikeIds(favoriteHikeIds);
        const error = await response.json();
        throw new Error(error.detail);
      }

      const data = await response.json();

      // Confirm server state
      const confirmedIds = new Set(favoriteHikeIds);
      if (data.is_favorited) {
        confirmedIds.add(hikeId);
      } else {
        confirmedIds.delete(hikeId);
      }
      setFavoriteHikeIds(confirmedIds);

      // Update is_favorited in cached hike arrays
      const updateHikeArray = (hikes) =>
        hikes.map(hike =>
          hike.id === hikeId
            ? { ...hike, is_favorited: data.is_favorited }
            : hike
        );

      setRegisteredHikesData(prev => updateHikeArray(prev));
      setOrganizedHikes(prev => updateHikeArray(prev));

      return data.is_favorited;
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  };

  const isRegistered = (hikeId) => registeredHikes.includes(hikeId);

  // NEW: Helper methods for cached data
  const isFavorite = (hikeId) => favoriteHikeIds.has(hikeId);
  const getParticipationStatus = (hikeId) => participationMap[hikeId] || null;

  // Organizer helpers
  const isOrganizerOf = (hikeId) => organizedHikes.some(h => h.id === hikeId);
  const totalPendingRequests = organizedHikes.reduce(
    (sum, hike) => sum + (hike.pending_count || 0), 0
  );
  const getOrganizerStats = (hikeId) => {
    const hike = organizedHikes.find(h => h.id === hikeId);
    if (!hike) return null;
    return {
      pending_count: hike.pending_count || 0,
      approved_count: hike.approved_count || 0,
      max_partecipanti: hike.max_partecipanti
    };
  };

  // Permission helpers
  const hasPermission = (permissionName) => {
    // system:admin bypasses all permission checks
    if (permissions.includes('system:admin')) return true;
    return permissions.includes(permissionName);
  };

  const hasAnyPermission = (permissionNames) => {
    if (permissions.includes('system:admin')) return true;
    return permissionNames.some(p => permissions.includes(p));
  };

  const checkAuth = useCallback(async () => {
    const savedToken = localStorage.getItem('access_token');
    if (savedToken) {
      setToken(savedToken);
      try {
        // Use optimized single-call profile loading
        await loadUserProfile(savedToken);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('access_token');
        setToken(null);
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const error = await response.json();
        // Handle both FastAPI (detail) and Chalice (Message) error formats
        throw new Error(error.detail || error.Message || error.message || 'Registration failed');
      }

      return userData.email;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        // Handle both FastAPI (detail) and Chalice (Message) error formats
        throw new Error(error.detail || error.Message || error.message || 'An error occurred during login');
      }

      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('access_token', data.access_token);

      // NEW: Single API call to get user + registered hikes + favorites + organized hikes
      await loadUserProfile(data.access_token);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setRegisteredHikes([]);
      setFavoriteHikeIds(new Set());
      setParticipationMap({});
      setOrganizedHikes([]);
      setPermissions([]);
      localStorage.removeItem('access_token');
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    register,
    login,
    logout,
    updateUser,
    loadUserProfile,

    // Registration/participation
    registeredHikes,
    registeredHikesData,
    registerForHike,
    unregisterFromHike,
    isRegistered,
    getParticipationStatus,
    participationMap,

    // Favorites (NEW)
    favoriteHikeIds,
    toggleFavorite,
    isFavorite,

    // Organized hikes (NEW)
    organizedHikes,
    isOrganizerOf,
    totalPendingRequests,
    getOrganizerStats,

    // User role (legacy - for backward compatibility during migration)
    isOrganizer: user?.is_organizer || user?.is_admin,
    isAdmin: user?.is_admin,

    // New role system
    role: user?.role,

    // Permissions (new fine-grained system)
    permissions,
    hasPermission,
    hasAnyPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
